'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { BadgeCheck, ArrowRight, MessageCircle } from 'lucide-react'

/* ─────────────────────────────────────────────────────────────
   UmmahRail — a compact teaser of the ONE national community feed
   (GET /api/feed), for embedding in the directory rail and masjid
   detail page. NOT masjid/area scoped.

   HONESTY: the feed is BLENDED. A masjid item (actor==='masjid')
   shows the verified BadgeCheck; a USER post (actor==='user') shows
   the author name with NO badge and NO masjid link — never cross
   them. (Deliberately does NOT reuse kit.tsx's FeedPostCard, which
   hardcodes a badge on every author.)

   Renders inner content only (rows + footer); the host wraps it in
   its own card chrome (PremiumCard on directory, a section on the
   masjid page). Both compose nudges route to /feed unconditionally
   — /feed shows the open composer to members and a sign-in prompt
   to guests, so no auth probe is needed here.
───────────────────────────────────────────────────────────── */

interface RailItem {
  id: string
  kind: string
  actor?: 'masjid' | 'user'
  type: string
  title: string
  body: string
  masjid?: string
  masjidSlug?: string
  authorName?: string
  category?: string
  time: string
  href: string
}

const CATEGORY_LABEL: Record<string, string> = {
  ask: 'Ask', dua: 'Dua', recommend: 'Recommend', lost_found: 'Lost & found', help: 'Help', sadaqah_jariyah: 'Sadaqah Jariyah', blood: 'Blood request', achievement: 'Achievement',
}

const LIMIT = 4

export default function UmmahRail() {
  const [items, setItems] = useState<RailItem[]>([])
  const [phase, setPhase] = useState<'loading' | 'ready' | 'empty'>('loading')

  useEffect(() => {
    let alive = true
    fetch(`/api/feed?offset=0&limit=${LIMIT}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return
        const list: RailItem[] = Array.isArray(d?.items) ? d.items.slice(0, LIMIT) : []
        setItems(list)
        setPhase(list.length ? 'ready' : 'empty')
      })
      .catch(() => { if (alive) setPhase('empty') })
    return () => { alive = false }
  }, [])

  return (
    <div className="ur">
      {phase === 'loading' ? (
        <div className="ur-skel">
          {Array.from({ length: 3 }).map((_, i) => <div key={i} className="ur-skel-row" />)}
        </div>
      ) : phase === 'empty' ? (
        <p className="ur-empty">No posts yet — be the first to share with the ummah.</p>
      ) : (
        <ul className="ur-list">
          {items.map((it) => {
            const isUser = it.actor === 'user'
            const name = isUser ? (it.authorName || 'Member') : (it.masjid || 'Masjids.in')
            const chip = isUser && it.category && CATEGORY_LABEL[it.category] ? CATEGORY_LABEL[it.category] : null
            const text = (it.body || it.title || '').trim()
            // User rows are not linkable to a masjid; masjid rows link to the profile/source.
            const rowHref = isUser ? '/feed' : (it.masjidSlug ? `/masjids/${it.masjidSlug}` : it.href || '/feed')
            return (
              <li key={it.id} className="ur-row">
                <Link href={rowHref} className="ur-rowlink">
                  <span className={'ur-av' + (isUser ? ' user' : '')} aria-hidden>{name.charAt(0).toUpperCase()}</span>
                  <span className="ur-rowbody">
                    <span className="ur-rowhead">
                      <b className="ur-name">{name}</b>
                      {/* HONESTY: verified badge ONLY for masjid actors */}
                      {!isUser && <BadgeCheck size={12} strokeWidth={1.6} className="ur-badge" />}
                      {chip && <span className={'ur-chip ur-chip-' + it.category}>{chip}</span>}
                      <span className="ur-time">{it.time}</span>
                    </span>
                    {text && <span className="ur-text">{text}</span>}
                  </span>
                </Link>
              </li>
            )
          })}
        </ul>
      )}

      <div className="ur-foot">
        <Link href="/feed" className="ur-see">See all <ArrowRight size={12} strokeWidth={2} /></Link>
        <Link href="/feed" className="ur-ask"><MessageCircle size={12} strokeWidth={1.8} /> Ask the community</Link>
      </div>

      <style jsx>{`
        .ur{display:block}
        .ur-list{list-style:none;margin:0;padding:0;display:grid;gap:11px}
        .ur-row{margin:0}
        .ur :global(.ur-rowlink){display:flex;gap:9px;align-items:flex-start;text-decoration:none;color:inherit}
        .ur-av{width:28px;height:28px;border-radius:50%;flex:none;display:grid;place-items:center;font-size:11px;font-weight:700;color:#fff;background:linear-gradient(135deg,var(--f-green),var(--f-green-dark))}
        .ur-av.user{background:linear-gradient(135deg,var(--f-teal),var(--f-green))}
        .ur-rowbody{flex:1;min-width:0;display:block}
        .ur-rowhead{display:flex;align-items:center;gap:5px;flex-wrap:wrap;min-width:0}
        .ur-name{font-family:var(--f-display);font-weight:600;font-size:12px;color:var(--f-navy);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:130px}
        .ur :global(.ur-badge){color:var(--f-teal);flex:none}
        .ur-chip{font-family:var(--f-mono);font-size:8px;letter-spacing:.06em;font-weight:700;text-transform:uppercase;border-radius:99px;padding:2px 7px;background:rgba(14,124,97,.1);color:var(--f-green-deep)}
        .ur-chip-dua{background:rgba(212,175,55,.16);color:var(--f-gold-deep)}
        .ur-chip-help{background:rgba(209,67,67,.12);color:#B23232}
        .ur-time{font-size:10px;color:var(--f-slate-dim);margin-left:auto;white-space:nowrap}
        .ur-text{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;font-size:11.5px;color:var(--f-slate);line-height:1.45;margin-top:2px}

        .ur-skel{display:grid;gap:11px}
        .ur-skel-row{height:34px;border-radius:9px;background:linear-gradient(100deg,#EFECE3 30%,#F7F4EC 50%,#EFECE3 70%);background-size:200% 100%;animation:urs 1.4s linear infinite}
        @keyframes urs{from{background-position:200% 0}to{background-position:-200% 0}}
        .ur-empty{font-size:11.5px;color:var(--f-slate);line-height:1.5;margin:0}

        .ur-foot{display:flex;align-items:center;gap:10px;margin-top:12px;padding-top:10px;border-top:1px solid var(--f-line)}
        .ur :global(.ur-see){display:inline-flex;align-items:center;gap:4px;font-size:11.5px;font-weight:700;color:var(--f-green);text-decoration:none}
        .ur :global(.ur-ask){display:inline-flex;align-items:center;gap:5px;font-size:11.5px;font-weight:600;color:var(--f-slate);text-decoration:none;margin-left:auto}
        .ur :global(.ur-ask:hover),.ur :global(.ur-see:hover){color:var(--f-green-deep)}
      `}</style>
    </div>
  )
}
